import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { resendVerificationEmail, isValidRedirectRoute } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions as fbFunctions } from '../config/firebase';

export default function VerifyEmailPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState('');

  // Get the redirect path with priority: query param > location state > default to /library
  const from = React.useMemo(() => {
    // First, check for 'from' query parameter
    const params = new URLSearchParams(location.search);
    const fromParam = params.get('from');
    
    if (fromParam && isValidRedirectRoute(fromParam)) {
      console.log('[VerifyEmailPage] Using from query param:', fromParam);
      return fromParam;
    }
    
    // Fall back to location.state (from ProtectedRoute or previous navigation)
    if (location.state?.from?.pathname && isValidRedirectRoute(location.state.from.pathname)) {
      console.log('[VerifyEmailPage] Using from location.state.pathname:', location.state.from.pathname);
      return location.state.from.pathname;
    }
    if (location.state?.from && typeof location.state.from === 'string' && isValidRedirectRoute(location.state.from)) {
      console.log('[VerifyEmailPage] Using from location.state:', location.state.from);
      return location.state.from;
    }
    
    // Default to library
    console.log('[VerifyEmailPage] No valid from found, using /library');
    return '/library';
  }, [location.search, location.state]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    // Not signed in; send to register/login with the ?from param preserved
    const params = new URLSearchParams(location.search);
    const fromParam = params.get('from');
    const registerPath = fromParam ? `/register?from=${encodeURIComponent(fromParam)}` : '/register';
    navigate(registerPath, { replace: true });
    return null;
  }

  const handleResend = async () => {
    setMessage('Sending verification email...');
    const { error } = await resendVerificationEmail();
    if (error) {
      console.error('email test here - resend verification failed:', error);
      setMessage('Failed to send verification email. Please try again in a moment.');
    } else {
      console.info('email test here - resend verification requested for', user.email);
      setMessage('Verification email sent. Please check your inbox and spam folder.');
    }
  };

  const handleCheck = async () => {
    setMessage('Checking verification status...');
    try {
      await auth.currentUser.reload();
      const refreshed = auth.currentUser;
      console.info('email test here - reload complete. verified:', !!refreshed?.emailVerified);
      if (refreshed?.emailVerified) {
        // Attempt to award one-time verification credits (idempotent on server)
        try {
          const award = httpsCallable(fbFunctions, 'awardVerificationCredits');
          await award();
          setMessage('Email verified! 300 Credits added to your account. Redirecting...');
        } catch (awardErr) {
          console.error('Failed to award verification credits:', awardErr);
          // Proceed even if awarding fails; user is verified
          setMessage('Email verified! Redirecting...');
        }
        navigate(from, { replace: true });
      } else {
        setMessage('Still not verified. Please click the link in your email.');
      }
    } catch (e) {
      console.error('email test here - reload failed:', e);
      setMessage('Failed to check status. Please try again.');
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-4">Verify your email</h1>
      <p className="text-gray-700 mb-4">
        We sent a verification link to <span className="font-medium">{user.email}</span>.
        Please click the link to verify your account.
      </p>
      {message && (
        <div className="mb-4 p-2 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded">
          {message}
        </div>
      )}
      <div className="flex gap-3">
        <button
          onClick={handleResend}
          className="bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700"
        >
          Resend verification email
        </button>
        <button
          onClick={handleCheck}
          className="bg-gray-100 text-gray-800 py-2 px-4 rounded hover:bg-gray-200"
        >
          I verified, continue
        </button>
      </div>
    </div>
  );
}
